import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TuneIcon from '@mui/icons-material/Tune';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ScheduleIcon from '@mui/icons-material/Schedule';
import dayjs from 'dayjs';
import A1 from 'assets/A1.svg?react';
import A2 from 'assets/A2.svg?react';
import B1 from 'assets/B1.svg?react';
import B2 from 'assets/B2.svg?react';
import C1 from 'assets/C1.svg?react';
import C2 from 'assets/C2.svg?react';
import { VocabularyTestHistory } from 'app/types';
import { fmt, levelOf } from 'pages/VocabularyTest/cefr';
import CEFRScaleBar from 'pages/VocabularyTest/components/CEFRScaleBar';
import FilledActionButton from 'components/FilledActionButton';
import { VocabularyTestScope } from 'pages/VocabularyTest/useVocabularyTestScope';
import VocabularyTestScopeSummary from 'pages/VocabularyTest/components/VocabularyTestScopeSummary';
import VocabularyTestScopePanel from 'pages/VocabularyTest/components/VocabularyTestScopePanel';

interface VocabularyTestHeroCardProps {
  learnedCount: number;
  history: VocabularyTestHistory[];
  onStart: () => void;
  onToggleScope: () => void;
  scopeOpen: boolean;
  scope: VocabularyTestScope;
  starting?: boolean;
}

const LEVEL_ICONS: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  A1,
  A2,
  B1,
  B2,
  C1,
  C2,
};

const MetaItem: React.FC<{ icon: React.ReactNode; text: string }> = ({
  icon,
  text,
}) => (
  <Stack
    direction={'row'}
    alignItems={'center'}
    spacing={0.75}
    sx={{ color: 'text.secondary', fontSize: 13.5 }}
  >
    {icon}
    <Box component={'span'}>{text}</Box>
  </Stack>
);

const VocabularyTestHeroCard: React.FC<VocabularyTestHeroCardProps> = ({
  learnedCount,
  history,
  onStart,
  onToggleScope,
  scopeOpen,
  scope,
  starting,
}) => {
  // On open, reveal the scope panel (it renders below the fold); on collapse,
  // return to the top of the page. Tracks the previous open state so we only
  // scroll on an actual transition, not on every re-render.
  const scopePanelRef = useRef<HTMLDivElement | null>(null);
  const prevScopeOpenRef = useRef(scopeOpen);
  useEffect(() => {
    if (scopeOpen === prevScopeOpenRef.current) return;
    prevScopeOpenRef.current = scopeOpen;
    if (scopeOpen) {
      const el = scopePanelRef.current;
      if (el) {
        const top =
          el.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [scopeOpen]);

  const completed = useMemo(
    () => history.filter((t) => t.status === 'DONE'),
    [history],
  );
  const chronological = useMemo(
    () =>
      [...completed].sort(
        (a, b) => dayjs(a.updatedAt).valueOf() - dayjs(b.updatedAt).valueOf(),
      ),
    [completed],
  );

  const cur = levelOf(learnedCount);

  const earliest = chronological[0];
  const latest = chronological[chronological.length - 1];
  const delta =
    chronological.length >= 2 ? latest.learnedCount - earliest.learnedCount : 0;
  const months =
    chronological.length >= 2
      ? Math.max(
          0,
          dayjs(latest.updatedAt).diff(dayjs(earliest.updatedAt), 'month'),
        )
      : 0;

  const LevelIcon = LEVEL_ICONS[cur.id];

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'primary.tableBorder',
        borderRadius: 2,
        boxShadow: '0 14px 36px -28px rgba(20,20,20,0.4)',
        p: { xs: 2.5, md: '28px 30px' },
      }}
    >
      {/* Top row: big stat + level art */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 2.5, md: 3.75 }}
        divider={
          <Divider
            orientation={'vertical'}
            flexItem
            sx={{ display: { xs: 'none', md: 'block' } }}
          />
        }
        alignItems={{ xs: 'flex-start', md: 'center' }}
      >
        {/* Stat */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: 'primary.main',
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: '0.09em',
              mb: 1.25,
              textTransform: 'uppercase',
            }}
          >
            {'Твій словниковий запас'}
          </Typography>
          <Stack
            direction={'row'}
            alignItems={'flex-end'}
            spacing={1.75}
            sx={{ mb: 0.75, flexWrap: 'wrap' }}
          >
            <Typography
              sx={{
                fontSize: { xs: 44, md: 56 },
                fontWeight: 800,
                letterSpacing: '-0.02em',
                lineHeight: 0.95,
              }}
            >
              {fmt(learnedCount)}
            </Typography>
            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: { xs: 15, md: 18 },
                pb: 0.75,
              }}
            >
              {'слів, які ти знаєш'}
            </Typography>
          </Stack>
          {delta > 0 && months > 0 && (
            <Stack
              direction={'row'}
              alignItems={'center'}
              spacing={0.5}
              sx={{ color: '#3B6D11', fontSize: 14, fontWeight: 700 }}
            >
              <TrendingUpIcon sx={{ fontSize: 17 }} />
              <Box component={'span'}>{`+${fmt(delta)} за ${months} міс`}</Box>
            </Stack>
          )}
        </Box>

        {/* Level art */}
        <Stack
          direction={'row'}
          alignItems={'center'}
          spacing={2}
          sx={{ flex: 'none', maxWidth: '100%' }}
        >
          {LevelIcon && (
            <Box
              sx={{
                flex: 'none',
                '& svg': { width: { xs: 64, md: 88 }, height: 'auto' },
              }}
            >
              <LevelIcon />
            </Box>
          )}
          <Box sx={{ maxWidth: 180, minWidth: 0 }}>
            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {'Орієнтовний рівень'}
            </Typography>
            <Typography
              sx={{
                color: 'primary.main',
                fontSize: { xs: 22, md: 26 },
                fontWeight: 800,
                lineHeight: 1.1,
              }}
            >
              {`${cur.id} · ${cur.name}`}
            </Typography>
            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: 13,
                lineHeight: 1.4,
                mt: 0.4,
              }}
            >
              {cur.desc}
            </Typography>
          </Box>
        </Stack>
      </Stack>

      <Divider sx={{ my: { xs: 2.5, md: '26px' } }} />

      {/* CEFR scale */}
      <CEFRScaleBar count={learnedCount} />

      {/* Actions row */}
      <Stack
        direction={{ xs: 'column-reverse', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1.5}
        sx={{ mt: { xs: 3, md: '26px' }, flexWrap: 'wrap', rowGap: 1.5 }}
      >
        <MetaItem
          icon={<ScheduleIcon sx={{ fontSize: 16 }} />}
          text={'~30 хв'}
        />
        <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
        <Button
          variant={'outlined'}
          startIcon={<TuneIcon />}
          onClick={onToggleScope}
          sx={{ height: 46, px: 2.75 }}
        >
          {scopeOpen ? 'Сховати вибір слів' : 'Обрати слова для тесту'}
        </Button>
        <FilledActionButton
          startIcon={<PlayArrowIcon />}
          onClick={onStart}
          disabled={starting}
          sx={{ fontSize: 14, height: 46, px: 2.75 }}
        >
          {'Пройти новий тест'}
        </FilledActionButton>
      </Stack>

      {(scopeOpen || !scope.isDefault) && (
        <Box sx={{ mt: 2 }}>
          <VocabularyTestScopeSummary
            scope={scope}
            open={scopeOpen}
            onToggle={onToggleScope}
          />
        </Box>
      )}
      {scopeOpen && (
        <Box ref={scopePanelRef}>
          <VocabularyTestScopePanel
            scope={scope}
            onClose={onToggleScope}
            onStart={onStart}
            startLabel={'Пройти новий тест'}
            starting={starting}
          />
        </Box>
      )}
    </Box>
  );
};

export default VocabularyTestHeroCard;
